import { Routes, Route } from 'react-router-dom';
import { lazy, Suspense, useContext, useEffect } from 'react';
import './App.css';
import Home from './component/Home';
import Footer from './component/Footer';
import PrivateRoute from './component/PrivateRoute';
import SEO from './component/SEO';
import { Toaster } from 'react-hot-toast';
import Loader from './component/loader/Loader';
import { SocketContext } from './context/SocketContext';
import { AuthContext } from './context/AuthContext';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { initNativePush, syncPushToken } from './utils/nativePush';

// Lazy-load route components so the landing page only loads Home + minimal JS (better LCP, TBT, bundle size)
const Login = lazy(() => import('./component/login/Login'));
const Register = lazy(() => import('./component/register/Register'));
const Dashboard = lazy(() => import('./component/dashboard/Dashboard'));
const ForgetPassword = lazy(() => import('./component/ForgetPassword'));
const SetNewPassword = lazy(() => import('./component/SetNewPassword'));
const SetPassword = lazy(() => import('./component/SetPassword'));
const CompletePhoneRegistration = lazy(() => import('./component/CompletePhoneRegistration'));
const CompleteGoogleProfile = lazy(() => import('./component/CompleteGoogleProfile'));
const NotFound = lazy(() => import('./component/NotFound'));
const HomePage = lazy(() => import('./component/scorecard/HomePage'));
const ScoreUpdate = lazy(() => import('./component/scorecard/ScoreUpdate'));
const CoinToss = lazy(() => import('./component/scorecard/CoinToss'));
const GuestMatchWizard = lazy(() => import('./component/scorecard/StartMatchGuest/GuestMatchWizard'));
const PlayerSelection = lazy(() => import('./component/scorecard/PlayerSelection'));
const LiveScore = lazy(() => import('./component/scorecard/liveScore/LiveScore'));
const MatchHighlights = lazy(() => import('./component/matchHighlights/MatchHighlights'));
const ContactUs = lazy(() => import('./component/ContactUs/ContactUs'));
const FeatureRequest = lazy(() => import('./component/FeatureRequest/FeatureRequest'));
const AdminLogin = lazy(() => import('./component/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./component/admin/AdminDashboard'));

const RouteFallback = () => <div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-hidden="true" />;

function App(userId) {
  const socket = useContext(SocketContext);
  const { isAuthenticated } = useContext(AuthContext);

  // Native push: register the device with FCM once on app start, and (re)send the
  // token to the backend whenever the user becomes authenticated. No-op on web.
  useEffect(() => {
    initNativePush();
  }, []);
  useEffect(() => {
    if (isAuthenticated) syncPushToken();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!socket) {
      console.log("Socket is not initialised yet");
    } else {
      socket.on("receiveNotification", (data) => {
        console.log("Notification received:", data);
        // Notifications are handled through the notification list component
      });
      socket.on("joinRoomRequest", (data) => {
        console.log("Join room request received:", data);
        // Team invitations are handled through the notification list component
        // Users can accept/decline from their notifications page
      });

      socket.emit("userOnline", userId.userId, (response) => {
        console.log(response);
      });
    }
  }, [socket]);

  // Hardware back button (Android): navigate router history instead of instantly
  // closing the app. Only exit when there's no in-app history to go back to.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let handle;
    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack && window.history.length > 1) {
        window.history.back();
      } else {
        CapacitorApp.exitApp();
      }
    }).then((listener) => { handle = listener; });
    return () => { if (handle) handle.remove(); };
  }, []);

  return (
    <>
      <SEO />
      <Loader />
      <Toaster position="top-right"
        reverseOrder={false} />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/feature-request" element={<FeatureRequest />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgetpassword" element={<ForgetPassword />} />
        <Route path="/setnewpassword" element={<SetNewPassword />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/complete-phone-registration" element={<CompletePhoneRegistration />} />
        <Route path="/complete-google-profile" element={<CompleteGoogleProfile />} />
        <Route path="/dashboard/*" element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
          } />
        <Route path="/scorecard/:teamA/vs/:teamB/:tossWinner/:overs/:matchID/scoreUpdate/*" element={<ScoreUpdate />} />
        <Route path="/scorecard/:teamA/vs/:teamB/:overs/tossCoin" element={<CoinToss />} />
        <Route path="/match/:matchId/player-selection" element={<PlayerSelection />} />
        <Route path="/scorecard" element={<HomePage />} />
        <Route path="/scorecard/start-match/*" element={<GuestMatchWizard />} />
        <Route path='/scorecard/:matchID/livescore/*' element={<LiveScore/>}/>
        <Route path='/match/highlights/:match_id' element={<MatchHighlights/>}/>
        {/* <Route path="/groupprofile/:roomid/*" element={<GroupProfile />} /> */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      <Footer />
    </>
  );
}

export default App;
